'use client';

import { usePathname } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';

import { chatsService } from '@/src/entities/chats/api/service';
import { getUserIdFromAccessToken } from '@/src/shared/lib/access-token';
import { useAuthStore } from '@/src/shared/model/auth-store';

import { notifyIncomingMessage } from '../lib/notify';
import {
  applyChatAccess,
  applyMatchUpdate,
  applyMessagesRead,
  applyPresence,
  replaceMessage,
  upsertMessage,
} from '../model/cache';
import { useOpenChatStore } from '../model/open-chat';
import { useTypingStore } from '../model/typing';
import { useChatSocket } from '../model/useChatSocket';

export function ChatRealtime() {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((state) => state.accessToken);
  const role = useAuthStore((state) => state.role);
  const userId = accessToken ? getUserIdFromAccessToken(accessToken) : null;

  useChatSocket({
    onMessage: (message) => {
      upsertMessage(queryClient, message, userId);

      const openChatId = useOpenChatStore.getState().chatId;
      const visibleHere = !document.hidden && openChatId === message.chatId;

      if (userId && message.senderId !== userId && visibleHere) {
        void chatsService.markRead(message.chatId).then((result) => {
          applyMessagesRead(queryClient, result, userId);
        });
      }

      if (userId && message.senderId !== userId && !visibleHere) {
        notifyIncomingMessage(
          message.text,
          message.kind === 'SYSTEM' ? 'Понятно' : 'Новое сообщение',
          { sound: pathname !== '/chats' },
        );
      }
    },
    onRead: (payload) => {
      applyMessagesRead(queryClient, payload, userId);
    },
    onMatch: (payload) => {
      applyMatchUpdate(queryClient, payload, role);
    },
    onPresence: (payload) => {
      applyPresence(queryClient, payload);
    },
    onTyping: (payload) => {
      if (payload.userId === userId) {
        return;
      }

      useTypingStore.getState().setTyping(payload.chatId, payload.isTyping);
    },
    onMessageUpdated: (message) => {
      replaceMessage(queryClient, message);
    },
    onAccess: (payload) => {
      applyChatAccess(queryClient, payload);
    },
  });

  return null;
}
